import { useCallback, useEffect, useState } from 'react';
import { Link } from "react-router-dom";

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { formatDate, handleErrors } from '../../utils/common';

function Warehouse() {
    const Swal = window.Swal;

    const [warehouses, setWarehouses] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchWarehouses = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const limit = 20;
            const res = await api.get('/warehouse/list', {
                params: {
                    search: debouncedSearch,
                    limit,
                    offset: (page - 1) * limit
                }
            });

            if (res.status === 200) {
                setWarehouses(res.data.warehouses);
                const totalPagesCount = Math.ceil(res.data.total / limit);
                setTotalPages(totalPagesCount > 0 ? totalPagesCount : 1);
            }
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        fetchWarehouses(currentPage);
    }, [currentPage, fetchWarehouses]);

    const handleDelete = (slug) => {
        Swal.fire({
            title: 'Are you sure?',
            html: '<i class="fas fa-trash-alt" style="font-size: 42px; color: #d33; margin-bottom: 10px;"></i><p>You would not be able to revert this!</p>',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.delete(`/warehouse/${slug}`);
                    if (res.status === 200) {
                        showToast('success', res.data.message, 'Success');
                        setWarehouses((prev) => prev.filter((w) => w.slug !== slug));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    const generatePagination = () => {
        const pages = [];
        const maxVisible = 5;
        const sidePages = 2;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > sidePages + 2) {
                pages.push('...');
            }

            const start = Math.max(2, currentPage - sidePages);
            const end = Math.min(totalPages - 1, currentPage + sidePages);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - sidePages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.WAREHOUSE}>Warehouse</Link>
                    </li>
                </ol>
            </div>
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0">Warehouse</h4>
                            <Link to={ROUTES.ADD_WAREHOUSE} className="btn btn-primary btn-sm">
                                <i className="fas fa-plus me-2"></i>Add New
                            </Link>
                        </div>
                        <div className="card-body">
                            <div className="row d-flex justify-content-start">
                                <div className="col-md-4">
                                    <div className="input-group search-area">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search Warehouse..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <span className="input-group-text">
                                            <i className="flaticon-381-search-2"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th className="width80"><strong>#</strong></th>
                                                <th><strong>Warehouse</strong></th>
                                                <th><strong>Location</strong></th>
                                                <th><strong>Created At</strong></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {warehouses && warehouses.map((warehouse, index) => (
                                                <tr key={warehouse._id || index}>
                                                    <td><strong>{index + 1}</strong></td>
                                                    <td>{warehouse?.title || '-'}</td>
                                                    <td>{warehouse?.location || '-'}</td>
                                                    <td>{formatDate(warehouse?.t)}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <Link to={`/warehouse/${warehouse.slug}/edit`} className="btn btn-primary shadow btn-xs sharp me-1">
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Link>
                                                            <a
                                                                href="#/"
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                onClick={() => handleDelete(warehouse.slug)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>

                                {generatePagination().map((page, index) =>
                                    page === '...' ? (
                                        <li className="page-item disabled" key={index}>
                                            <span className="page-link">...</span>
                                        </li>
                                    ) : (
                                        <li className={`page-item ${currentPage === page ? 'active' : ''}`} key={index}>
                                            <a className="page-link" href="#/" onClick={() => setCurrentPage(page)}>{page}</a>
                                        </li>
                                    )
                                )}

                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Warehouse;
